import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRailtelPricedPlansForOperator, railtelRecharge } from "../../api/railtelApi";
import "./RailtelRechargeModal.css";

/**
 * RailTel's own recharge API has no "which package" option — it just
 * renews whatever's already configured on their side for that
 * subscriber. So this isn't a picker anymore (an admin choosing a
 * mismatched plan here used to mean charging one price while RailTel
 * silently renewed a totally different package). Instead this shows a
 * preview of what will actually be charged — matched by the
 * subscriber's current package name — and the backend independently
 * re-derives that same match server-side before charging anything.
 */
const RailtelRechargeModal = ({ open, onClose, username, packageName, operatorId, customerId, onSuccess }) => {
  const [matchedPlan, setMatchedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notMapped, setNotMapped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchAndMatch = async () => {
      setLoading(true);
      setMatchedPlan(null);
      setNotMapped(false);
      try {
        const { data } = await getRailtelPricedPlansForOperator(customerId);
        if (cancelled) return;
        const plans = data?.plans || [];
        const match = plans.find(
          (p) => (p.railtel_package_name || "").trim().toLowerCase() === (packageName || "").trim().toLowerCase()
        );
        if (match) {
          setMatchedPlan(match);
        } else {
          setNotMapped(true);
        }
      } catch (err) {
        toast.error("Failed to load RailTel pricing");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAndMatch();
    return () => {
      cancelled = true;
    };
  }, [open, operatorId, packageName]);

  if (!open) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      // The backend re-fetches the subscriber and re-matches the price
      // itself — this preview is purely informational, not the source
      // of truth for what gets charged.
      // The preview above is fetched via operatorId purely for display;
      // the actual charge is re-derived server-side from customer_id ->
      // operator, so a stale/incorrect operatorId here can't affect
      // what's actually charged.
      const { data } = await railtelRecharge(username, customerId);
      if (data?.success) {
        toast.success(data.message || "RailTel account recharged");
        onSuccess?.();
        onClose();
      } else {
        toast.error(data?.message || "Recharge failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Server error during recharge");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="railtel-modal-overlay" onClick={onClose}>
      <div className="railtel-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="railtel-modal__title">Top-up RailTel Account</h3>
        <p className="railtel-modal__subtitle">{username}</p>

        {loading ? (
          <p className="railtel-modal__loading">Checking current package…</p>
        ) : notMapped ? (
          <p className="railtel-modal__empty">
            No price is mapped for this subscriber's current package
            {packageName ? <> (<strong>{packageName}</strong>)</> : ""}. Ask your admin to add this
            exact package name to your RailTel plans before recharging.
          </p>
        ) : matchedPlan ? (
          <div className="railtel-modal__plan-list">
            <div className="railtel-modal__plan-option selected">
              <div className="railtel-modal__plan-info">
                <span className="railtel-modal__plan-name">{matchedPlan.plan_name}</span>
                <span className="railtel-modal__plan-duration">
                  {matchedPlan.duration_months} Month{matchedPlan.duration_months > 1 ? "s" : ""} ·{" "}
                  {matchedPlan.railtel_package_name}
                </span>
              </div>
              <span className="railtel-modal__plan-price">₹{matchedPlan.price}</span>
            </div>
            <p className="railtel-modal__note">
              This is the package currently active on RailTel for this subscriber — recharge always
              renews whatever's already set on their side, it can't change the package.
            </p>
          </div>
        ) : null}

        <div className="railtel-modal__actions">
          <button className="railtel-btn railtel-btn--outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="railtel-btn railtel-btn--primary"
            onClick={handleConfirm}
            disabled={submitting || loading || notMapped || !matchedPlan}
          >
            {submitting ? "Processing…" : "Confirm Recharge"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RailtelRechargeModal;
